import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import { useAuth } from './AuthContext';

const LocationContext = createContext(null);

export function LocationProvider({ children }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(() => {
    const stored = localStorage.getItem('selectedLocationId');
    return stored ? parseInt(stored, 10) : null;
  });
  const [loading, setLoading] = useState(true);

  // Fetch accessible locations when user is authenticated
  useEffect(() => {
    if (!user) {
      setLocations([]);
      setSelectedLocationId(null);
      setLoading(false);
      return;
    }

    axios.get('/api/locations')
      .then(res => {
        const locs = res.data.locations || [];
        setLocations(locs);

        // Validate stored selection or pick default
        const stored = localStorage.getItem('selectedLocationId');
        const storedId = stored ? parseInt(stored, 10) : null;
        const validSelection = locs.find(l => l.id === storedId);

        if (validSelection) {
          setSelectedLocationId(validSelection.id);
        } else {
          // Pick the default location, or the first one
          const defaultLoc = locs.find(l => l.is_default) || locs[0];
          const newId = defaultLoc?.id || null;
          setSelectedLocationId(newId);
          if (newId) localStorage.setItem('selectedLocationId', String(newId));
          else localStorage.removeItem('selectedLocationId');
        }
      })
      .catch(() => {
        setLocations([]);
      })
      .finally(() => setLoading(false));
  }, [user]);

  const selectLocation = useCallback((locationId) => {
    setSelectedLocationId(locationId);
    if (locationId) {
      localStorage.setItem('selectedLocationId', String(locationId));
    } else {
      localStorage.removeItem('selectedLocationId');
    }
    // Invalidate all queries so they refetch with the new location
    queryClient.invalidateQueries();
  }, [queryClient]);

  const currentLocation = locations.find(l => l.id === selectedLocationId) || null;

  return (
    <LocationContext.Provider value={{
      locations,
      selectedLocationId,
      selectLocation,
      currentLocation,
      loading,
    }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext() {
  const context = useContext(LocationContext);
  if (!context) throw new Error('useLocationContext must be used within LocationProvider');
  return context;
}
